import { useState, useEffect } from "react";
import MyTable from "../../Components/MyTable";
import Mytitle from "../../Components/Mytitle";
import MyInput from "../../Components/Myinput";
import { useTranslation } from "react-i18next";
import { toLocal } from "../../utils/toLocal";
import { Modal } from "react-bootstrap";
import ModalForm from "../../Components/ModalForm";
import Can from "../../Components/Can";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {  faEye } from "@fortawesome/free-solid-svg-icons";

function AccountStatement() {
  const { t } = useTranslation();

  // دالة مساعدة لتنسيق الأرقام السالبة والموجبة حسب المطلوب
  const formatNumberWithColor = (value) => {
    const num = Number(value);
    if (isNaN(num)) return "-";

    if (num < 0) {
      const absoluteFormatted = toLocal(Math.abs(num));
      return (
        <span
          className="text-danger fw-bold"
          style={{ direction: "ltr", display: "inline-block" }}
        >
          ({absoluteFormatted})
        </span>
      );
    }

    return <span>{toLocal(num)}</span>;
  };

  const [filters, setFilters] = useState({
    from: "",
    to: "",
    type: "",
  });
  const [cashBalance, setCashBalance] = useState(0);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // حالات التحكم في نافذة كشف الحساب التفصيلي لشخص محدد
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);

  // فلتر التاريخ الخاص بجدول الحركات جوه المودال (منفصل عن فلتر الصفحة الرئيسية)
  const [modalFrom, setModalFrom] = useState("");
  const [modalTo, setModalTo] = useState("");

  const isEnglish = localStorage.getItem("i18nextLng") === "en";

  // عند الضغط على زر عرض، نفتح المودال ونخزن البيانات ونضع تواريخ الشهر الحالي كديفولت
  const handleViewDetails = (row) => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    setSelectedAccount(row);
    setModalFrom(formatDate(firstDay));
    setModalTo(formatDate(lastDay));
    setShowViewModal(true);
  };

  // دالة موحدة لترجمة نوع الحركة سواء كانت مصروف أو عميل/مورد
  const getInvoiceTypeLabel = (isExpense, type) => {
    if (!type) return "-";
    if (isExpense) {
      switch (type) {
        case "send":
          return t("account_send_money_invoice");
        case "receive":
          return t("account_receive_money_invoice");
        default:
          return t(type, type);
      }
    }
    switch (type) {
      case "invoice_service":
        return t("account_service_invoice");
      case "send_money_invoice":
        return t("account_send_money_invoice");
      case "receive_money_invoice":
        return t("account_receive_money_invoice");
      case "opening_balance":
        return t("account_opening_balance");
      default:
        return t(type, type);
    }
  };

  const totalValue = (value) => {
    setCashBalance(value);
  };

  const columns = [
    {
      header: t("account_name"),
      accessor: (row) =>
        row.expense?.name || row.customer?.name || row.supplier?.name || "-",
    },
    {
      header: t("account_invoice_number"),
      accessor: (row) => row?.last_transaction?.invoice_id ?? "-",
    },
    {
      header: t("account_invoice_type"),
      accessor: (row) => {
        const isExpense = !!row.expense;
        const type = row?.last_transaction?.type;
        return getInvoiceTypeLabel(isExpense, type);
      },
    },
    // {
    //   header: t("account_amount"),
    //   accessor: (row) => {
    //     const val =
    //       row?.last_transaction?.amount ?? row?.last_transaction?.value;
    //     return val !== undefined && val !== null
    //       ? formatNumberWithColor(val)
    //       : "-";
    //   },
    // },
    {
      header: t("account_total"),
      accessor: (row) => {
        const val =
          row?.last_transaction?.total_value ?? row?.last_transaction?.amount;
        return val !== undefined && val !== null
          ? formatNumberWithColor(val)
          : "-";
      },
    },
    {
      header: t("actions"),
      accessor: (row) => (
        <Can permission="account_statement_show">
          <button
            className="btn btn-sm mx-1 no-style"
            onClick={() => handleViewDetails(row)}
          >
            <FontAwesomeIcon icon={faEye} />
          </button>
        </Can>
      ),
    },
  ];

  // تعيين التواريخ الافتراضية للشهر الحالي
  useEffect(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    setFilters((prev) => ({
      ...prev,
      to: formatDate(lastDay),
      from: formatDate(firstDay),
    }));
  }, []);

  // التحديث التلقائي للجدول عند تغيير فلاتر البحث الرئيسي
  useEffect(() => {
    if (filters.type && filters.from && filters.to) {
      setAppliedFilters(filters);
      setRefreshKey((prev) => prev + 1);
    } else {
      setAppliedFilters(null);
    }
  }, [filters.type, filters.from, filters.to]);

  // استخراج بيانات كارت الشخص/المصروف المفتوح حالياً في المودال
  const accountInfo =
    selectedAccount?.customer ||
    selectedAccount?.supplier ||
    selectedAccount?.expense;
  const isExpenseModal = !!selectedAccount?.expense;

  // فلترة جدول الحركات جوه المودال حسب التاريخ من/إلى
  const filteredTransactions = (selectedAccount?.transactions || []).filter(
    (trx) => {
      if (!modalFrom && !modalTo) return true;
      const trxDate = new Date(trx.date);
      if (modalFrom && trxDate < new Date(modalFrom)) return false;
      if (modalTo && trxDate > new Date(modalTo)) return false;
      return true;
    },
  );

  return (
    <div className="container mt-4">
      <Mytitle title={t("account_title")} />

      <div
        style={{ direction: !isEnglish ? "ltr" : "rtl" }}
        className="row flex-row-reverse g-3 align-items-end mb-4"
      >
        <div className="col-md-4">
          <MyInput
            as="select"
            label={t("account_select_type")}
            value={filters.type}
            onChange={(e) => setFilters({ ...filters, type: e.target.value })}
            options={[
              { value: "", label: t("account_select_type") },
              { value: "customer", label: t("account_customers") },
              { value: "supplier", label: t("suppliers") },
              { value: "expense", label: t("account_expenses") },
            ]}
          />
        </div>

        <div className="col-md-4">
          <MyInput
            label={t("account_from")}
            type="date"
            value={filters.from}
            onChange={(e) => setFilters({ ...filters, from: e.target.value })}
            dontShowDay={true}
          />
        </div>

        <div className="col-md-4">
          <MyInput
            label={t("account_to")}
            type="date"
            value={filters.to}
            onChange={(e) => setFilters({ ...filters, to: e.target.value })}
            dontShowDay={true}
          />
        </div>
      </div>

      {appliedFilters && (
        <div className="mt-4">
          <MyTable   
            resource="account-statement"
            method="post"
            title={t("account_title")}
            body={appliedFilters}
            columns={columns}
            refreshKey={refreshKey}
            cashBalance={cashBalance}
            setCashBalance={setCashBalance}
            component="accountStatement"
            totalValue={totalValue}
          />
        </div>
      )}

      {/* ==================== نافذة كشف الحساب التفصيلية ==================== */}
      <ModalForm
        show={showViewModal}
        onClose={() => setShowViewModal(false)}
        size="lg"
        mode="print"
        centered
      >
        <Modal.Header
          className="border-0 pt-4 px-4"
          style={{ borderRadius: "15px 15px 0 0" }}
        >
          <h4
            className="modal-title w-100 text-center fw-bold text-dark"
            style={{ color: "#374151" }}
          >
            {t("account_statement_for", "كشف حساب")} ({accountInfo?.name || "-"}
            )
          </h4>
        </Modal.Header>

        <Modal.Body className="p-4">
          {selectedAccount ? (
            <div>
              {/* شاشات عرض الكروت بناءً على نوع المودال الحالي (مصروفات أم عملاء وموردين) */}
              {isExpenseModal ? (
                // إذا كان مصروفات: يظهر الاسم والوصف فقط في كارتين (col-md-6 ليكون التصميم متناسقاً)
                <div className="row g-3 text-center mb-4">
                  <div className="col-md-6">
                    <div className="p-3 rounded-3 border shadow-sm h-100 d-flex flex-column justify-content-center">
                      <span className="small fw-bold mb-1">
                        {t("name_label", "الاسم")}
                      </span>
                      <h6 className="fw-bold mb-0">
                        {accountInfo?.name || "-"}
                      </h6>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="p-3 rounded-3 border shadow-sm h-100 d-flex flex-column justify-content-center">
                      <span className="small fw-bold mb-1">
                        {t("description_label", "الوصف")}
                      </span>
                      <h6 className="fw-bold mb-0">
                        {accountInfo?.description || "-"}
                      </h6>
                    </div>
                  </div>
                </div>
              ) : (
                // إذا كان عميل أو مورد: يظهر الاسم والجوال والعنوان (3 كروت بالتساوي)
                <div className="row g-3 text-center mb-4">
                  <div className="col-md-4">
                    <div className="p-3 rounded-3 border shadow-sm h-100 d-flex flex-column justify-content-center">
                      <span className="small fw-bold mb-1">
                        {t("name_label", "الاسم")}
                      </span>
                      <h6 className="fw-bold mb-0">
                        {accountInfo?.name || "-"}
                      </h6>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="p-3 rounded-3 border shadow-sm h-100 d-flex flex-column justify-content-center">
                      <span className="small fw-bold mb-1">
                        {t("phone_label", "الجوال")}
                      </span>
                      <h6 className="fw-bold mb-0">
                        {accountInfo?.phone || accountInfo?.mobile || "-"}
                      </h6>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="p-3 rounded-3 border shadow-sm h-100 d-flex flex-column justify-content-center">
                      <span className="small fw-bold mb-1">
                        {t("address_label", "العنوان")}
                      </span>
                      <h6 className="fw-bold mb-0">
                        {accountInfo?.address || "-"}
                      </h6>
                    </div>
                  </div>
                </div>
              )}

              <hr className="my-3 text-muted" />

              {/* فلتر التاريخ الخاص بجدول الحركات جوه المودال */}
              <div className="d-flex flex-wrap justify-content-end gap-3 mb-4 d-print-none">
                <div style={{ minWidth: "160px" }}>
                  <MyInput
                    label={t("account_from")}
                    type="date"
                    value={modalFrom}
                    onChange={(e) => setModalFrom(e.target.value)}
                    dontShowDay={true}
                  />
                </div>
                <div style={{ minWidth: "160px" }}>
                  <MyInput
                    label={t("account_to")}
                    type="date"
                    value={modalTo}
                    onChange={(e) => setModalTo(e.target.value)}
                    dontShowDay={true}
                  />
                </div>
              </div>

              {/* جدول الحركات الخاص بالشخص/المصروف المحدد */}
              <div className="table-responsive rounded-3 border bg-white shadow-sm">
                <table className="table table-hover text-center align-middle mb-0">
                  <thead
                    style={{ backgroundColor: "#f8fafc", color: "#475569" }}
                  >
                    <tr>
                      <th className="py-3 fw-bold">م</th>
                      <th className="py-3 fw-bold">
                        {t("account_date", "التاريخ")}
                      </th>
                      <th className="py-3 fw-bold">
                        {t("account_amount", "القيمه")}
                      </th>
                      <th className="py-3 fw-bold">
                        {t("account_total", "إجمالي القيمة")}
                      </th>
                      <th className="py-3 fw-bold">
                        {t("account_invoice_type", "نوع الفواتير")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTransactions.length > 0 ? (
                      filteredTransactions.map((trx, idx) => (
                        <tr key={trx.uuid || trx.id || idx}>
                          <td>{idx + 1}</td>
                          <td>{trx.date || "-"}</td>
                          <td>{toLocal(trx.amount ?? trx.value ?? 0)}</td>
                          <td className="fw-bold">
                            {toLocal(
                              trx.total_value ?? trx.amount ?? trx.value ?? 0,
                            )}
                          </td>
                          <td>
                            {trx.type === "opening_balance"
                              ? t("account_opening_balance", "رصيد افتتاحي")
                              : getInvoiceTypeLabel(isExpenseModal, trx.type)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="py-3 text-muted">
                          {t("no_data", "لا توجد بيانات")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              {t("loading", "جاري التحميل...")}
            </div>
          )}
        </Modal.Body>
      </ModalForm>
    </div>
  );
}

export default AccountStatement;
